import React, { useState } from 'react';
import { X, Video, Info, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const VideoUploadHelp = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-purple-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Video className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Video Upload Guide</h2>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Recommended Format */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-green-900 text-lg mb-2">Recommended: MP4 Format</h3>
                <p className="text-green-800">
                  For best compatibility across all devices and browsers, always use <strong>MP4 (H.264 codec)</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Supported Formats */}
          <div>
            <h3 className="font-bold text-gray-900 text-lg mb-3">Supported Formats</h3>
            <div className="space-y-2">
              <FormatItem format="MP4" support="Best" color="green" description="Works everywhere - Recommended!" />
              <FormatItem format="WebM" support="Good" color="blue" description="Modern browsers" />
              <FormatItem format="MOV" support="Good" color="blue" description="iPhone/Mac videos" />
              <FormatItem format="AVI" support="Limited" color="yellow" description="May not work on all devices" />
              <FormatItem format="MPEG" support="Limited" color="yellow" description="May not work on all devices" />
            </div>
          </div>

          {/* File Size */}
          <div>
            <h3 className="font-bold text-gray-900 text-lg mb-3">File Size Limit</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700">
                <strong>Maximum:</strong> 50MB per video
              </p>
              <p className="text-sm text-gray-600 mt-2">
                💡 Tip: Keep videos under 30MB for faster uploads and better performance
              </p>
            </div>
          </div>

          {/* Convert Videos */}
          <div>
            <h3 className="font-bold text-gray-900 text-lg mb-3">How to Convert Videos</h3>
            <div className="space-y-3">
              <ConversionOption
                title="Online (Easiest)"
                tool="CloudConvert"
                link="https://cloudconvert.com"
                description="Free online converter - No software needed"
              />
              <ConversionOption
                title="Desktop (Best Quality)"
                tool="HandBrake"
                link="https://handbrake.fr"
                description="Free software for Windows/Mac/Linux"
              />
              <ConversionOption
                title="iPhone/Mac"
                tool="QuickTime"
                link=""
                description="Built-in - Export as 1080p"
              />
            </div>
          </div>

          {/* Samsung Browser Note */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-yellow-900 mb-1">Samsung Browser Users</h4>
                <p className="text-yellow-800 text-sm">
                  Videos work best in <strong>Chrome browser</strong>. If videos don't play in Samsung Browser, 
                  try Chrome or ensure videos are MP4 (H.264) format.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div>
            <h3 className="font-bold text-gray-900 text-lg mb-3">Quick Tips</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start space-x-2">
                <span className="text-pink-500 font-bold">•</span>
                <span>Keep videos short (30-90 seconds) for better engagement</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-pink-500 font-bold">•</span>
                <span>Record in good lighting for better quality</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-pink-500 font-bold">•</span>
                <span>Use landscape (horizontal) mode when recording</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-pink-500 font-bold">•</span>
                <span>Test your video before posting</span>
              </li>
            </ul>
          </div>

          {/* Troubleshooting */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <h4 className="font-bold text-red-900 mb-2">Video Not Working?</h4>
            <ul className="text-sm text-red-800 space-y-1">
              <li>✓ Convert to MP4 format</li>
              <li>✓ Check file size is under 50MB</li>
              <li>✓ Try uploading from Chrome browser</li>
              <li>✓ Compress video if too large</li>
            </ul>
          </div>

          {/* Close Button */}
          <Button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-3"
          >
            Got It!
          </Button>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const FormatItem = ({ format, support, color, description }) => {
  const colors = {
    green: 'bg-green-100 text-green-800 border-green-300',
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300'
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-3">
        <span className="font-mono font-bold text-gray-900">{format}</span>
        <span className={`text-xs px-2 py-1 rounded-full border ${colors[color]}`}>
          {support} Support
        </span>
      </div>
      <span className="text-sm text-gray-600">{description}</span>
    </div>
  );
};

const ConversionOption = ({ title, tool, link, description }) => {
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-1">
        <h5 className="font-semibold text-gray-900">{title}</h5>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Visit →
          </a>
        )}
      </div>
      <p className="text-sm text-gray-600">{tool}</p>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
};

export default VideoUploadHelp;
